import type { DesignPerson, DesignScopeRoot } from "./designUtils";

/**
 * Design fields as held by the create/edit forms. Every field is optional
 * because the edit page leaves out people the caller may not change.
 */
export type DesignMutationFormInput = {
  baselineWorkspaceId?: string | null | undefined;
  designWorkspaceId?: string | null;
  name?: string | null;
  designType?: string | null | undefined;
  sponsors?: DesignPerson[] | null;
  leads?: DesignPerson[] | null;
  participants?: DesignPerson[] | null;
  scopeRoots?: DesignScopeRoot[] | null;
  scopeRootGroupIds?: string[] | null;
  primaryDriver?: string | null;
  purpose?: string | null;
  successCriteria?: string | null;
  benefits?: string | null;
  workflowStage?: string | null;
  startDate?: string | null;
  targetImplementationDate?: string | null;
  baselineDataDate?: string | null;
};

/** Matches the API `DesignInput` accepted by `createDesign` and `updateDesign`. */
export type DesignMutationInput = {
  baselineWorkspaceId?: string | null | undefined;
  designWorkspaceId: string | null;
  name?: string | null | undefined;
  designType?: string | null | undefined;
  sponsorPersonIds?: string[] | undefined;
  leadPersonIds?: string[] | undefined;
  participantPersonIds?: string[] | undefined;
  scopeRootGroupIds?: string[] | null | undefined;
  primaryDriver?: string | null | undefined;
  purpose?: string | null | undefined;
  successCriteria?: string | null | undefined;
  benefits?: string | null | undefined;
  workflowStage?: string | null | undefined;
  startDate: string | null;
  targetImplementationDate: string | null;
  baselineDataDate: string | null;
};

export const personIdsForDesignInput = (
  people?: (DesignPerson | null | undefined)[] | null
): string[] =>
  (people || [])
    .map((p) => p?.aggregateId)
    .filter((aggregateId): aggregateId is string => Boolean(aggregateId));

const optionalDate = (dateValue?: string | null): string | null =>
  dateValue || null;

const samePeople = (
  next?: (DesignPerson | null | undefined)[] | null,
  loaded?: (DesignPerson | null | undefined)[] | null
): boolean => {
  const a = personIdsForDesignInput(next);
  const b = personIdsForDesignInput(loaded);
  return a.length === b.length && a.every((id, index) => id === b[index]);
};

type OmitUnchangedDesignPeopleOptions = {
  canEditPeople?: boolean;
};

const shouldOmitPeopleField = (
  next: DesignPerson[] | null | undefined,
  loaded: DesignPerson[] | null | undefined,
  { canEditPeople = true }: OmitUnchangedDesignPeopleOptions
): boolean => !canEditPeople || samePeople(next, loaded);

/**
 * Drop people arrays that are unchanged from the loaded design.
 *
 * The API treats the mere presence of any people field as a people update
 * (`hasDesignPeopleUpdate`) and then enforces the people-write bar, which is
 * WAC on the *tenant default* workspace - a different, stricter bar than design
 * edit access (WAC on the design's own workspace). The edit form always holds
 * populated people arrays, so re-submitting them unchanged 403s any caller who
 * only edited structure fields. Omitting them leaves the stored people
 * untouched: `designDatabase.updateDesign` only writes the `people` column when
 * one of the three role arrays is present.
 */
export const omitUnchangedDesignPeople = <T extends DesignMutationFormInput>(
  input: T,
  loaded?: DesignMutationFormInput | null,
  options: OmitUnchangedDesignPeopleOptions = {}
): T => ({
  ...input,
  sponsors: shouldOmitPeopleField(input.sponsors, loaded?.sponsors, options)
    ? undefined
    : input.sponsors,
  leads: shouldOmitPeopleField(input.leads, loaded?.leads, options)
    ? undefined
    : input.leads,
  participants: shouldOmitPeopleField(
    input.participants,
    loaded?.participants,
    options
  )
    ? undefined
    : input.participants,
});

/** Whether `email` is one of the design's sponsors, leads, or participants. */
export const isDesignPerson = (
  input: Pick<DesignMutationFormInput, "sponsors" | "leads" | "participants">,
  email: string | null | undefined
): boolean => {
  const wanted = email?.trim().toLowerCase();
  if (!wanted) return false;
  return [
    ...(input.sponsors || []),
    ...(input.leads || []),
    ...(input.participants || []),
  ].some((person) => person.email?.trim().toLowerCase() === wanted);
};

/** Client-side save gate. The API still accepts a partial design. */
export const designSaveMissingMessage = (
  input: Pick<
    DesignMutationFormInput,
    | "sponsors"
    | "scopeRoots"
    | "primaryDriver"
    | "startDate"
    | "targetImplementationDate"
  >
): string | null => {
  const missing: string[] = [];
  if (!input.sponsors?.length) missing.push("a sponsor");
  if (!input.scopeRoots?.length) missing.push("an in-scope business area");
  if (!input.primaryDriver?.trim()) missing.push("a primary driver");
  if (!input.startDate?.trim()) missing.push("a start date");
  if (!input.targetImplementationDate?.trim())
    missing.push("a target implementation date");
  return missing.length ? `Add ${missing.join(", ")} before saving.` : null;
};

export const buildDesignMutationInput = (
  input: DesignMutationFormInput
): DesignMutationInput => ({
  baselineWorkspaceId: input.baselineWorkspaceId,
  designWorkspaceId: input.designWorkspaceId || null,
  name: input.name,
  designType: input.designType,
  sponsorPersonIds: input.sponsors
    ? personIdsForDesignInput(input.sponsors)
    : undefined,
  leadPersonIds: input.leads ? personIdsForDesignInput(input.leads) : undefined,
  participantPersonIds: input.participants
    ? personIdsForDesignInput(input.participants)
    : undefined,
  scopeRootGroupIds: input.scopeRoots
    ? input.scopeRoots.map((r) => r.groupId)
    : input.scopeRootGroupIds,
  primaryDriver: input.primaryDriver,
  purpose: input.purpose,
  successCriteria: input.successCriteria,
  benefits: input.benefits,
  workflowStage: input.workflowStage,
  startDate: optionalDate(input.startDate),
  targetImplementationDate: optionalDate(input.targetImplementationDate),
  baselineDataDate: optionalDate(input.baselineDataDate),
});
